/* global google */

import styled, { CSSObject } from '@emotion/styled'
import { useContext, useEffect, useState, useRef } from 'react'
import { v4 as uuidv4 } from 'uuid'
import AddressInputField from '../Landing/Delivery/AddressInputField'
import InputFieldNew from 'shop/components/Controls/InputFieldNew'
import { RiMapPinLine as PinIcon } from 'react-icons/ri'
import { StyledHTMLElement } from 'shop/theme/types'
import DeliverySignIn from '../Landing/Delivery/DeliverySignIn'
import { useMediaQueries, useModal } from 'shop/hooks'
import { LandingContextNew } from '../Landing/LandingContext'
import usePlacesAutocomplete from 'use-places-autocomplete'

type AddressAutoCompleteProps = {
  value: string
  onChange: (value: string) => void
  onSelect: (value: string) => void
  onFocus?: () => void
  onBlur?: () => void
  withIcon?: boolean
  isEditing?: boolean
  newFulfillmentModal?: boolean
}

const AddressAutoComplete = ({
  withIcon,
  isEditing,
  newFulfillmentModal,
  value,
  onChange,
  onSelect,
  onBlur,
  onFocus
}: AddressAutoCompleteProps) => {
  const isLoggedIn = localStorage.getItem('customerId') !== null
  const { openModal } = useModal()
  const { isMobile } = useMediaQueries()
  const [activeIndex, setActiveIndex] = useState<number | null>(null)
  const { setIsOverlayOpen, setIsLoginOpen } = useContext(LandingContextNew)
  const sessionToken = useRef(uuidv4())

  const searchOptions = {
    location: new google.maps.LatLng({ lat: 51.5137137, lng: -0.1285204 }),
    radius: 30,
    sessiontoken: sessionToken.current
  }

  const {
    suggestions: { data: suggestions },
    setValue,
    clearSuggestions
  } = usePlacesAutocomplete({
    requestOptions: searchOptions,
    debounce: 500,
    defaultValue: value
  })

  useEffect(() => {
    const parent = document.getElementById('landing-form')
    if (!parent) return

    const suggestions = document.querySelectorAll('.suggestion-item')
    if (suggestions.length === 0) return

    const lastSuggestion = suggestions[suggestions.length - 1]
    if (!lastSuggestion) return

    const offset = lastSuggestion.getBoundingClientRect().top
    parent.scrollTo({ top: offset, behavior: 'smooth' })
  })

  useEffect(() => {
    const strippedAddressLength = value.replace(/\s/g, '')
    const isAddressEligible = strippedAddressLength.length >= 3

    if (isAddressEligible) {
      setValue(value)
      setActiveIndex(null)
      return
    }

    if (!isAddressEligible) clearSuggestions()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value])

  const handleOpenLoginModal = () => {
    openModal('login')

    isMobile && setIsOverlayOpen(true)
    // Differences because FullscreenModal on mobile & CenterModal on desktop
    !isMobile && setIsOverlayOpen(false)
    setIsLoginOpen(true)
  }

  const handleSuggestionSelect = async (
    suggestion: google.maps.places.AutocompletePrediction
  ) => {
    onSelect(suggestion.description)
    clearSuggestions()
    setActiveIndex(null)
  }

  return (
    <AutoCompleteInputGroup
      newFulfillmentModal={!!newFulfillmentModal}
      data-testid='address-autocomplete'
    >
      <>
        {newFulfillmentModal ? (
          <>
            <InputFieldNew
              name='input-field'
              icon={withIcon ? <PinIcon /> : null}
              hasValue={!!value}
              value={value}
              inputProps={{
                autoComplete: 'chrome-off',
                placeholder: 'e.g. N3 1LJ',
                value,
                autoFocus: true,
                onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
                  onChange(e.target.value),
                onFocus,
                onBlur
              }}
            />
            <LineBreak />
          </>
        ) : (
          <AddressInputField
            withIcon={!!withIcon}
            isEditing={isEditing}
            autoFocus={true}
            value={value}
            inputProps={{
              onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
                onChange(e.target.value),
              onFocus,
              onBlur
            }}
          />
        )}
      </>
      <div className='autocomplete-dropdown'>
        {suggestions.map((suggestion, i) => {
          const isActive = i === activeIndex
          const className = isActive
            ? 'suggestion-item suggestion-item-active'
            : 'suggestion-item'

          if (newFulfillmentModal) {
            return (
              <div
                data-testid={`addressSuggestion`}
                className={className}
                key={`address-suggestion-${i}`}
                onClick={() => handleSuggestionSelect(suggestion)}
                onMouseEnter={() => setActiveIndex(i)}
                onMouseLeave={() => setActiveIndex(null)}
              >
                <SuggestionDescriptionContainer>
                  <PinIcon />
                  <span>{suggestion.description}</span>
                </SuggestionDescriptionContainer>
              </div>
            )
          } else {
            return (
              <div
                data-testid={`addressSuggestion`}
                className={className}
                key={`address-suggestion-${i}`}
                onClick={() => handleSuggestionSelect(suggestion)}
                onMouseEnter={() => setActiveIndex(i)}
                onMouseLeave={() => setActiveIndex(null)}
              >
                <span>{suggestion.description}</span>
              </div>
            )
          }
        })}
        {newFulfillmentModal && !isLoggedIn && (
          <DeliverySignInCTAContainer onClick={handleOpenLoginModal}>
            <DeliverySignIn />
          </DeliverySignInCTAContainer>
        )}
      </div>
    </AutoCompleteInputGroup>
  )
}

const LineBreak = styled.hr<StyledHTMLElement>(
  (): CSSObject => ({
    position: 'absolute',
    right: '0',
    left: '0',
    height: '2px',
    backgroundColor: '#F0F0F0',
    marginTop: '24px',
    border: 'none'
  })
)

/** Sets auto complete styles for newFulfillmentModal */
const variantStyles = {
  newFulfillmentModal: (theme: any) => ({
    position: 'unset',
    width: '100%',
    '.autocomplete-dropdown': {
      marginTop: '25px',
      fontSize: theme.fontSizes[1],
      color: theme.colors.textBody,
      width: '100%',
      zIndex: 101,
      position: 'absolute',
      left: 0,
      background: 'white',
      paddingBottom: '32px',
      borderRadius: '16px'
    },
    '.suggestion-item': {
      background: '#FFFFFF',
      cursor: 'pointer',
      padding: '12px 18px',
      margin: 0,
      color: theme.colors.body,
      [theme.mediaQueries.viewport5]: {
        padding: '12px 34px'
      }
    },
    '.suggestion-item:last-child': {
      borderBottom: '2px solid #F0F0F0'
    },
    '.suggestion-item-active': {
      background: '#F2F2F2'
    },
    '.suggestion-item:active': {
      span: {
        color: '#4975E9'
      }
    }
  })
}

/** Returns newFulfillmentModal styles based on boolean prop */
const returnNewFulfillmentModalStyle = (
  theme: any,
  newFulfillmentModal: boolean
) => (newFulfillmentModal ? variantStyles['newFulfillmentModal'](theme) : {})

const AutoCompleteInputGroup = styled.div<
  StyledHTMLElement & { newFulfillmentModal: boolean }
>(({ theme, newFulfillmentModal }) => ({
  position: 'relative',
  '.autocomplete-dropdown': {
    position: 'absolute',
    marginTop: '8px',
    fontSize: theme.fontSizes[1],
    color: theme.colors.textBody,
    width: '100%',
    zIndex: 10
  },
  '.suggestion-item': {
    background: '#ffffff',
    cursor: 'pointer',
    padding: '12px 24px',
    margin: 0,
    color: theme.colors.body
  },
  '.suggestion-item:last-child': {
    marginBottom: '100px'
  },
  '.suggestion-item-active': {
    background: '#f1f1f1',
    borderRadius: theme.borderRadius
  },
  ...returnNewFulfillmentModalStyle(theme, newFulfillmentModal)
}))

const SuggestionDescriptionContainer = styled.div<StyledHTMLElement>(
  (): CSSObject => ({
    display: 'flex',
    alignItems: 'center',
    svg: {
      fill: '#595959',
      width: '15px',
      height: '15px',
      marginRight: '8px',
      flexShrink: 0
    }
  })
)

const DeliverySignInCTAContainer = styled.div<StyledHTMLElement>(
  (): CSSObject => ({
    marginTop: '32px',
    display: 'flex',
    justifyContent: 'center'
  })
)

export default AddressAutoComplete
